import { ChangeEvent, FC, useContext, useMemo } from "react";
import {
  CountyRef,
  FilterDescriptor,
  NetworkPortfolioManagementProjectActionStatus,
  NetworkPortfolioManagementProjectStatus,
  networkPortfolioManagementProjectStatusDisplayLabel,
  NetworkPortfolioManagementProjectTypeEnum,
  networkPortfolioManagementProjectTypeDisplayLabel,
  previewGatedProjectStatuses,
  ProjectActionFilter,
  projectActionDisplayLabel,
  projectStatusFilterOrder,
  selectableProjectTypes,
  SpecialtyGroupRef,
} from "@app/network-portfolio-management/interfaces";
import { useFilterContext } from "@app/network-portfolio-management/contexts/FilterContext";
import { useFilterOptions } from "@app/network-portfolio-management/hooks/useFilterOptions";
import {
  countyKey,
  getCustomSelectInputText,
  getFullySelectedStates,
  parseCountyKey,
  serviceAreasToNestedItems,
  specialtyGroupItemKey,
  specialtyGroupsByItemKey,
  specialtyOptionLabel,
  tagsToNestedItems,
  toDropdownItems,
  toSpecialtyDropdownItems,
} from "@app/network-portfolio-management/components/filterBarHelpers";
import { MoreFiltersMenu } from "@app/network-portfolio-management/components/MoreFiltersMenu";
import { SavedViewsButton } from "@app/network-portfolio-management/components/SavedViewsButton";
import { useFilterOverflow } from "@app/network-portfolio-management/hooks/useFilterOverflow";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import MultiselectNestedDropdown from "@app/shared/checkbox-dropdown/MultiselectNestedDropdown";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { OptionsListOption } from "@app/shared/qes-standards/forms/OptionsList";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { UserContext } from "@app/user/UserContext";
import Roles from "@app/user/roles";

// Measurement ids for the always-inline chrome that isn't a FilterDescriptor. Kept stable so the
// overflow hook can reserve their widths alongside the pinned filters.
const SAVED_VIEWS_ID = "saved-views";
const SEARCH_ID = "search";

export const FilterBar: FC = () => {
  const { filterOptions, filterOptionsError } = useFilterOptions();
  const { activeFilters, setActiveFilters: updateActiveFilters, canUsePreviewStatuses } = useFilterContext();
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const savedViewsEnabled = enabledFeatureFlags.has(Features.PROJECT_ARGUS_PROJECT_DASHBOARD_SAVED_VIEWS);

  const isDisabled = filterOptionsError || !filterOptions;

  // Project Utilization and Project Action are gated to Quest and Client Admin users. Visibility is
  // resolved here (rather than a RoleRestricted wrapper inside a descriptor's render()) so an
  // unauthorized user has no descriptor at all — the "More Filters" overflow math must count only
  // filters that actually render. Mirrors RoleRestricted.canRender for an allowedRoles-only gate.
  const user = useContext(UserContext);
  const canSeeRestrictedFilters =
    !!user && [Roles.QUEST_USER, Roles.CLIENT_ADMIN].some((role) => user.roles.includes(role));

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    updateActiveFilters((prev) => ({ ...prev, searchTerm: val }));
  };

  const handleSearchClear = () => {
    updateActiveFilters((prev) => ({ ...prev, searchTerm: "" }));
  };

  // Service Area selections are absorbed into `counties` by the nested dropdown.
  // State level filtering will be added in WI 192992.
  const projectTypeItems = useMemo(
    () => selectableProjectTypes.map((type) => ({
      key: type as string,
      name: networkPortfolioManagementProjectTypeDisplayLabel[type] ?? type,
    })),
    [],
  );
  const projectTypeNameByValue = useMemo(
    () => new Map(projectTypeItems.map((item) => [item.key, item.name])),
    [projectTypeItems],
  );
  const lobItems = useMemo(() => toDropdownItems(filterOptions?.linesOfBusiness), [filterOptions]);
  const lobNameById = useMemo(
    () => new Map(lobItems.map((item) => [item.key, item.name])),
    [lobItems],
  );
  const pucItems = useMemo(() => toDropdownItems(filterOptions?.projectUtilizations), [filterOptions]);
  const pucNameById = useMemo(
    () => new Map(pucItems.map((item) => [item.key, item.name])),
    [pucItems],
  );
  const serviceAreaItems = useMemo(
    () => serviceAreasToNestedItems(filterOptions?.serviceAreas),
    [filterOptions],
  );
  const totalCountyCount = useMemo(
    () => serviceAreaItems.reduce((n, state) => n + state.children.length, 0),
    [serviceAreaItems],
  );
  const projectNameOptions = useMemo<OptionsListOption[]>(
    () => (filterOptions?.projectNames ?? []).map((p) => ({ key: p.value, value: p.displayName })),
    [filterOptions],
  );
  const specialtyItems = useMemo(() => toSpecialtyDropdownItems(filterOptions?.specialties), [filterOptions]);
  // Resolves a selected dropdown key back to the exact group the API sent, so no component splits the option
  // value apart on its separator.
  const specialtyGroupByItemKey = useMemo(() => specialtyGroupsByItemKey(filterOptions?.specialties), [filterOptions]);
  const templateItems = useMemo(() => toDropdownItems(filterOptions?.templates), [filterOptions]);
  const tagItems = useMemo(() => tagsToNestedItems(filterOptions?.tags), [filterOptions]);
  const totalTagCount = useMemo(
    () => tagItems.reduce((n, group) => n + group.children.length, 0),
    [tagItems],
  );

  const selectedCountyKeys = useMemo(() => {
    const keys = activeFilters.serviceAreaCounties.map((c) => countyKey(c.state, c.county));
    if (filterOptions) {
      for (const stateAbbrev of activeFilters.serviceAreaStates) {
        const stateOpt = filterOptions.serviceAreas.find((s) => s.value === stateAbbrev);
        for (const county of stateOpt?.children ?? []) {
          keys.push(countyKey(stateAbbrev, county.value));
        }
      }
    }
    return keys;
  }, [activeFilters.serviceAreaCounties, activeFilters.serviceAreaStates, filterOptions]);

  const selectedTagKeys = useMemo(
    () => activeFilters.tagIds.map((id) => String(id)),
    [activeFilters.tagIds],
  );

  const projectTypeSelectionText = useMemo(
    () => getCustomSelectInputText(activeFilters.projectTypes.length, projectTypeItems.length),
    [activeFilters.projectTypes.length, projectTypeItems.length],
  );

  const lobSelectionText = useMemo(
    () => getCustomSelectInputText(activeFilters.linesOfBusinessIds.length, lobItems.length),
    [activeFilters.linesOfBusinessIds.length, lobItems.length],
  );

  const pucSelectionText = useMemo(
    () => getCustomSelectInputText(activeFilters.projectUtilizationCategoryIds.length, pucItems.length),
    [activeFilters.projectUtilizationCategoryIds.length, pucItems.length],
  );

  const selectedCountyCount = useMemo(() => {
    let count = activeFilters.serviceAreaCounties.length;
    if (filterOptions) {
      for (const stateAbbrev of activeFilters.serviceAreaStates) {
        const stateOpt = filterOptions.serviceAreas.find((s) => s.value === stateAbbrev);
        count += stateOpt?.children?.length ?? 0;
      }
    }
    return count;
  }, [activeFilters.serviceAreaCounties.length, activeFilters.serviceAreaStates, filterOptions]);

  const serviceAreaSelectionText = useMemo(
    () => getCustomSelectInputText(selectedCountyCount, totalCountyCount),
    [selectedCountyCount, totalCountyCount],
  );

  const specialtySelectionText = useMemo(
    () => getCustomSelectInputText(activeFilters.specialtyGroups.length, specialtyItems.length),
    [activeFilters.specialtyGroups.length, specialtyItems.length],
  );

  const templateSelectionText = useMemo(
    () => getCustomSelectInputText(activeFilters.templateNames.length, templateItems.length),
    [activeFilters.templateNames.length, templateItems.length],
  );

  const tagSelectionText = useMemo(
    () => getCustomSelectInputText(activeFilters.tagIds.length, totalTagCount),
    [activeFilters.tagIds.length, totalTagCount],
  );

  // Project Status options are an enum owned by the UI (like Project Action), in priority order; the two
  // Preview-gated statuses are only offered to Preview users.
  const projectStatusItems = useMemo(
    () => projectStatusFilterOrder
      .filter((status) => canUsePreviewStatuses || !previewGatedProjectStatuses.includes(status))
      .map((status) => ({ key: status, name: networkPortfolioManagementProjectStatusDisplayLabel[status] })),
    [canUsePreviewStatuses],
  );
  const projectStatusSelectionText = useMemo(
    () => getCustomSelectInputText(activeFilters.projectStatuses.length, projectStatusItems.length),
    [activeFilters.projectStatuses.length, projectStatusItems.length],
  );

  // Filter controls in render order. Pinned filters (Project Type, Line of Business, Service Area) always
  // stay inline; the rest are overflow candidates, in priority order, that collapse into the "More Filters"
  // fly-out once the row runs out of room — Specialty is listed first among them so it is the last to
  // collapse (it rarely needs to, but must be able to so the bar never overflows horizontally). Template is
  // listed immediately after Specialty since they share the same flat-string-list UI shape and both read as
  // project identity/metadata, ahead of Tags' free-form labeling. Project Utilization and Project Action are
  // included only for authorized users (see canSeeRestrictedFilters) so the overflow math counts only
  // actually-rendered filters.
  const filterDescriptors: FilterDescriptor[] = [
    {
      id: "project-type",
      isActive: activeFilters.projectTypes.length > 0,
      pinned: true,
      render: () => (
        <MultiselectDropdown<string>
          containerClassName="network-portfolio-management-filter-bar__item"
          customWidth
          customSelectInputText={projectTypeSelectionText}
          disabled={isDisabled}
          items={projectTypeItems}
          label="Project Type"
          onSelectionChanged={(selectedItems) =>
            updateActiveFilters({
              ...activeFilters,
              projectTypes: selectedItems.map((x) => x.key as NetworkPortfolioManagementProjectTypeEnum),
            })
          }
          selectedItems={activeFilters.projectTypes.map((value) => ({
            key: value,
            name: projectTypeNameByValue.get(value) ?? value,
          }))}
          showTooltipOnOverflow
          testId="npm-filter-project-type"
        />
      ),
    },
    {
      id: "line-of-business",
      isActive: activeFilters.linesOfBusinessIds.length > 0,
      pinned: true,
      render: () => (
        <MultiselectDropdown<string>
          containerClassName="network-portfolio-management-filter-bar__item"
          customWidth
          customSelectInputText={lobSelectionText}
          disabled={isDisabled}
          items={lobItems}
          label="Line of Business"
          onSelectionChanged={(selectedItems) =>
            updateActiveFilters({
              ...activeFilters,
              linesOfBusinessIds: selectedItems.map((x) => Number(x.key)),
            })
          }
          selectedItems={activeFilters.linesOfBusinessIds.map((id) => {
            const key = String(id);
            return { key, name: lobNameById.get(key) ?? key };
          })}
          showTooltipOnOverflow
          testId="npm-filter-lob"
        />
      ),
    },
    {
      id: "service-area",
      isActive: activeFilters.serviceAreaCounties.length > 0 || activeFilters.serviceAreaStates.length > 0,
      pinned: true,
      render: () => (
        <MultiselectNestedDropdown
          containerClassName="network-portfolio-management-filter-bar__item"
          customSelectInputText={serviceAreaSelectionText}
          disabled={isDisabled}
          items={serviceAreaItems}
          label="Service Area"
          onSelectionChanged={(selectedKeys) => {
            const allCounties = selectedKeys.map(parseCountyKey).filter((c): c is CountyRef => c !== null);
            if (!filterOptions) {
              updateActiveFilters({ ...activeFilters, serviceAreaCounties: allCounties, serviceAreaStates: [] });
              return;
            }
            const fullySelected = getFullySelectedStates(allCounties, filterOptions);
            updateActiveFilters({
              ...activeFilters,
              serviceAreaCounties: allCounties.filter((c) => !fullySelected.has(c.state)),
              serviceAreaStates: [...fullySelected],
            });
          }}
          selectedItems={selectedCountyKeys}
          showTooltipOnOverflow
          testId="npm-filter-service-area"
        />
      ),
    },
    {
      id: "specialty",
      isActive: activeFilters.specialtyGroups.length > 0,
      pinned: false,
      render: () => (
        <MultiselectDropdown<string>
          containerClassName="network-portfolio-management-filter-bar__item"
          customWidth
          customSelectInputText={specialtySelectionText}
          disabled={isDisabled}
          items={specialtyItems}
          label="Specialty Group"
          onSelectionChanged={(selectedItems) =>
            updateActiveFilters({
              ...activeFilters,
              specialtyGroups: selectedItems
                .map((x) => specialtyGroupByItemKey.get(x.key))
                .filter((group): group is SpecialtyGroupRef => group !== undefined),
            })
          }
          selectedItems={activeFilters.specialtyGroups.map((s) => ({
            key: specialtyGroupItemKey(s),
            name: specialtyOptionLabel(s.code, s.name),
          }))}
          showTooltipOnOverflow
          testId="npm-filter-specialty"
        />
      ),
    },
    {
      id: "template",
      isActive: activeFilters.templateNames.length > 0,
      pinned: false,
      render: () => (
        <MultiselectDropdown<string>
          containerClassName="network-portfolio-management-filter-bar__item"
          customWidth
          customSelectInputText={templateSelectionText}
          disabled={isDisabled}
          items={templateItems}
          label="Template"
          onSelectionChanged={(selectedItems) =>
            updateActiveFilters({
              ...activeFilters,
              templateNames: selectedItems.map((x) => x.key),
            })
          }
          selectedItems={activeFilters.templateNames.map((key) => ({ key, name: key }))}
          showTooltipOnOverflow
          testId="npm-filter-template"
        />
      ),
    },
    {
      id: "tags",
      isActive: activeFilters.tagIds.length > 0,
      pinned: false,
      render: () => (
        <MultiselectNestedDropdown
          containerClassName="network-portfolio-management-filter-bar__item"
          customSelectInputText={tagSelectionText}
          disabled={isDisabled}
          // Mirrors ECD's Tags filter: only individual tags are selectable; tag-group parents render as
          // accordion headers without their own checkbox. The shared component still exposes a global
          // "Select All" if the user wants every tag.
          hideParentCheckboxes
          items={tagItems}
          label="Tags"
          onSelectionChanged={(selectedKeys) =>
            updateActiveFilters({
              ...activeFilters,
              tagIds: selectedKeys
                .map((key) => Number(key))
                .filter((id): id is number => Number.isInteger(id)),
            })
          }
          selectedItems={selectedTagKeys}
          showTooltipOnOverflow
          testId="npm-filter-tags"
        />
      ),
    },
    {
      id: "project-status",
      isActive: activeFilters.projectStatuses.length > 0,
      pinned: false,
      render: () => (
        <MultiselectDropdown<string>
          containerClassName="network-portfolio-management-filter-bar__item"
          customWidth
          customSelectInputText={projectStatusSelectionText}
          disabled={isDisabled}
          items={projectStatusItems}
          label="Project Status"
          onSelectionChanged={(selectedItems) =>
            updateActiveFilters({
              ...activeFilters,
              projectStatuses: selectedItems.map((x) => x.key as NetworkPortfolioManagementProjectStatus),
            })
          }
          selectedItems={activeFilters.projectStatuses.map((status) => ({
            key: status,
            name: networkPortfolioManagementProjectStatusDisplayLabel[status],
          }))}
          showTooltipOnOverflow
          testId="npm-filter-project-status"
        />
      ),
    },
    // Role-gated overflow candidates — only present for authorized users (see canSeeRestrictedFilters).
    ...(canSeeRestrictedFilters
      ? [
          {
            id: "project-utilization",
            isActive: activeFilters.projectUtilizationCategoryIds.length > 0,
            pinned: false,
            render: () => (
              <MultiselectDropdown<string>
                containerClassName="network-portfolio-management-filter-bar__item"
                customWidth
                customSelectInputText={pucSelectionText}
                disabled={isDisabled}
                items={pucItems}
                label="Project Utilization"
                onSelectionChanged={(selectedItems) =>
                  updateActiveFilters({
                    ...activeFilters,
                    projectUtilizationCategoryIds: selectedItems.map((x) => Number(x.key)),
                  })
                }
                selectedItems={activeFilters.projectUtilizationCategoryIds.map((id) => {
                  const key = String(id);
                  return { key, name: pucNameById.get(key) ?? key };
                })}
                showTooltipOnOverflow
                testId="npm-filter-project-utilization"
              />
            ),
          },
          {
            id: "project-action",
            isActive: activeFilters.projectAction != null,
            pinned: false,
            render: () => (
              <QESSelect
                allowEmpty
                containerClassName="network-portfolio-management-filter-bar__item"
                disabled={isDisabled}
                label="Project Action"
                onChange={(e) => updateActiveFilters({ ...activeFilters, projectAction: (e.target.value || null) as ProjectActionFilter })}
                placeholder="All"
                testId="npm-filter-project-action"
                value={activeFilters.projectAction ?? ""}
              >
                <option value={NetworkPortfolioManagementProjectActionStatus.Run}>{projectActionDisplayLabel.run}</option>
                <option value={NetworkPortfolioManagementProjectActionStatus.Publish}>{projectActionDisplayLabel.publish}</option>
                <option value={NetworkPortfolioManagementProjectActionStatus.Archive}>{projectActionDisplayLabel.archive}</option>
              </QESSelect>
            ),
          },
        ]
      : []),
  ];

  const pinnedDescriptors = filterDescriptors.filter((d) => d.pinned);
  const optionalDescriptors = filterDescriptors.filter((d) => !d.pinned);
  const optionalById = new Map(optionalDescriptors.map((d) => [d.id, d]));

  const { hasOverflow, inlineOptionalIds, overflowOptionalIds, registerButton, registerContainer, registerItem } =
    useFilterOverflow({
      // Saved Views is only reserved when it renders — the overflow math must count only what is on the row.
      alwaysInlineIds: [...(savedViewsEnabled ? [SAVED_VIEWS_ID] : []), ...pinnedDescriptors.map((d) => d.id), SEARCH_ID],
      optionalIds: optionalDescriptors.map((d) => d.id),
    });

  const byId = (id: string): FilterDescriptor | undefined => optionalById.get(id);
  const inlineOptionalDescriptors = inlineOptionalIds.map(byId).filter((d): d is FilterDescriptor => d != null);
  const overflowDescriptors = overflowOptionalIds.map(byId).filter((d): d is FilterDescriptor => d != null);

  return (
    <>
      {filterOptionsError && (
        <QESAlert type="danger" persist={true}>
          Failed to load filter options, please try again. Contact Quest Client Services for assistance if error persists.
        </QESAlert>
      )}
      <div className="network-portfolio-management-filter-bar" data-testid="network-portfolio-management-filter-bar">
        <div className="network-portfolio-management-filter-bar__inner" ref={registerContainer}>
          {savedViewsEnabled && (
            <div className="network-portfolio-management-filter-bar__slot" ref={registerItem(SAVED_VIEWS_ID)}>
              <SavedViewsButton />
            </div>
          )}
          {pinnedDescriptors.map((descriptor) => (
            <div className="network-portfolio-management-filter-bar__slot" key={descriptor.id} ref={registerItem(descriptor.id)}>
              {descriptor.render()}
            </div>
          ))}
          {inlineOptionalDescriptors.map((descriptor) => (
            <div className="network-portfolio-management-filter-bar__slot" key={descriptor.id} ref={registerItem(descriptor.id)}>
              {descriptor.render()}
            </div>
          ))}
          {hasOverflow && <MoreFiltersMenu descriptors={overflowDescriptors} registerButton={registerButton} />}

          <div className="network-portfolio-management-search-input" data-testid="npm-search-input" ref={registerItem(SEARCH_ID)}>
            <QESTextInput
              aria-label="Search projects"
              hideLabel
              inputAppend={
                activeFilters.searchTerm.length > 0 ? (
                  <QESButton
                    aria-label="Clear search"
                    className="network-portfolio-management-search-input__clear-btn"
                    onClick={handleSearchClear}
                    qesTestId="npm-search-clear"
                    qesType="none"
                  >
                    <QesIcon description="Clear search" iconName={QesIcons.X} width="10" />
                  </QESButton>
                ) : undefined
              }
              label="Search projects"
              onChange={handleSearchChange}
              optionsListConfig={{
                maxResults: 5,
                minChars: 3,
                noMatchText: "No matching projects",
                options: projectNameOptions,
              }}
              placeholder="Search projects..."
              testId="npm-search-text-input"
              value={activeFilters.searchTerm}
            />
          </div>
        </div>
      </div>
    </>
  );
};
